import { Contract, Interface } from 'ethers'
import { cloneDeep } from 'lodash'
import { ethAddress, getAddress } from 'viem'
import type { Config } from 'wagmi'
import type { StoreApi } from 'zustand'
import type { ExchangeOutput, FormStatus, FormValues, RouterSwapOutput } from '@/dex/components/PagePool/Swap/types'
import {
  DEFAULT_EST_GAS,
  DEFAULT_EXCHANGE_OUTPUT,
  DEFAULT_FORM_STATUS,
  DEFAULT_FORM_VALUES,
} from '@/dex/components/PagePool/Swap/utils'
import type { EstimatedGas as FormEstGas } from '@/dex/components/PagePool/types'
import type { RoutesAndOutput } from '@/dex/components/PageRouterSwap/types'
import { curvejsApi } from '@/dex/lib/curvejs'
import { fetchPoolCurrencyReserves, type CurrencyReserves } from '@/dex/queries/pool-currency-reserves.query'
import type { State } from '@/dex/store/useStore'
import {
  ChainId,
  CurveApi,
  FnStepApproveResponse,
  FnStepEstGasApprovalResponse,
  FnStepResponse,
} from '@/dex/types/main.types'
import { getMaxAmountMinusGas } from '@/dex/utils/utilsGasPrices'
import { getSlippageImpact, getSwapActionModalType } from '@/dex/utils/utilsSwap'
import type { PoolTemplate } from '@curvefi/api/lib/pools'
import { useWallet } from '@evm-ui/features/connect-wallet'
import { fetchGasInfoAndUpdateLib } from '@evm-ui/queries/gas-info.query'
import { setMissingProvider } from '@evm-ui/utils/store.util'
import type { Nullish } from '@primitives/objects.utils'
import { fetchPoolTokenBalances } from '../hooks/usePoolTokenBalances'
import { getTokens } from '../pool.utils'
import { invalidatePoolInfo, invalidateUserPoolInfo } from '../queries/invalidation'

type StateKey = keyof typeof DEFAULT_STATE

type SliceState = {
  activeKey: string
  exchangeOutput: Record<string, ExchangeOutput>
  routerSwapOutput: Record<string, RouterSwapOutput>
  isMaxLoading: boolean
  ignoreExchangeRateCheck: Record<string, boolean>
  formEstGas: Record<string, FormEstGas>
  formStatus: FormStatus
  formValues: FormValues
}

const SLICE_KEY = 'poolSwap'

// prettier-ignore
export type PoolSwapSlice = {
  [SLICE_KEY]: SliceState & {
    fetchIgnoreExchangeRateCheck: (pool: PoolTemplate) => Promise<boolean>
    fetchExchangeOutput: (activeKey: string, storedActiveKey: string, config: Config, curve: CurveApi, pool: PoolTemplate, formValues: FormValues, maxSlippage: string) => Promise<void>
    fetchMaxAmount: (activeKey: string, config: Config, curve: CurveApi, pool: PoolTemplate, formValues: FormValues, maxSlippage: string) => Promise<string>
    setFormValues: (config: Config, curve: CurveApi | null, pool: PoolTemplate | undefined, updatedFormValues: Partial<FormValues>, isGetMaxFrom: boolean | null, isSeed: boolean | Nullish, maxSlippage: string) => Promise<void>

    // steps
    fetchEstGasApproval: (activeKey: string, chainId: ChainId, pool: PoolTemplate, formValues: FormValues, maxSlippage: string) => Promise<FnStepEstGasApprovalResponse | undefined>
    fetchStepApprove: (activeKey: string, config: Config, curve: CurveApi, pool: PoolTemplate, formValues: FormValues, globalMaxSlippage: string) => Promise<FnStepApproveResponse | undefined>
    fetchStepSwap: (activeKey: string, curve: CurveApi, pool: PoolTemplate, formValues: FormValues, maxSlippage: string) => Promise<FnStepResponse | undefined>

    setStateByActiveKey: <T>(key: StateKey, activeKey: string, value: T) => void
    setStateByKey: <T>(key: StateKey, value: T) => void
    setStateByKeys: (SliceState: Partial<SliceState>) => void
    resetState: (pool: PoolTemplate, isWrapped: boolean) => void
  }
}

const DEFAULT_STATE: SliceState = {
  activeKey: '',
  exchangeOutput: {},
  routerSwapOutput: {},
  isMaxLoading: false,
  ignoreExchangeRateCheck: {},
  formEstGas: {},
  formStatus: DEFAULT_FORM_STATUS,
  formValues: DEFAULT_FORM_VALUES,
}

export const createPoolSwapSlice = (
  _set: StoreApi<State>['setState'],
  get: StoreApi<State>['getState'],
): PoolSwapSlice => ({
  [SLICE_KEY]: {
    ...DEFAULT_STATE,

    fetchIgnoreExchangeRateCheck: async (pool: PoolTemplate) => {
      const state = get()
      const sliceState = state[SLICE_KEY]

      const storedIgnoreExchangeRateCheck = sliceState.ignoreExchangeRateCheck[pool.id]

      if (typeof storedIgnoreExchangeRateCheck !== 'undefined') {
        return storedIgnoreExchangeRateCheck
      } else {
        const provider = useWallet.getState().provider

        try {
          const json = await import('@/dex/components/PagePool/abis/stored_rates.json').then(module => module.default)
          const iface = new Interface(json)
          const contract = new Contract(pool.address, iface.format(), provider)
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Existing violation before enabling this rule.
          const storedRates = await contract.stored_rates()

          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- Existing violation before enabling this rule.
          const ignoreExchangeRateCheck = Object.values(storedRates).some(rate => {
            // if rate is > 1, then number cannot be checked for exchange rate
            const parsedRate = BigInt(rate as bigint)
              .toString()
              .replace(/0+$/, '')
            return Number(parsedRate) > 1
          })
          sliceState.setStateByActiveKey('ignoreExchangeRateCheck', pool.id, ignoreExchangeRateCheck)
          return ignoreExchangeRateCheck
        } catch (_error) {
          // ignore error, only stablswap ng pools have stored_rates
          sliceState.setStateByActiveKey('ignoreExchangeRateCheck', pool.id, false)
          return false
        }
      }
    },
    fetchExchangeOutput: async (activeKey, storedActiveKey: string, config, curve, pool, formValues, maxSlippage) => {
      const state = get()
      const sliceState = state[SLICE_KEY]

      const ignoreExchangeRateCheck = await sliceState.fetchIgnoreExchangeRateCheck(pool)
      const resp = await curvejsApi.poolSwap.exchangeOutput(
        activeKey,
        pool,
        formValues,
        maxSlippage,
        ignoreExchangeRateCheck,
      )

      const cFormStatus = cloneDeep(sliceState.formStatus)
      cFormStatus.error = ''
      cFormStatus.warning = ''

      if (resp.error) {
        get()[SLICE_KEY].setStateByKey('formStatus', { ...cFormStatus, error: resp.error })
      } else if (resp.activeKey === get()[SLICE_KEY].activeKey) {
        const cFormValues = cloneDeep(formValues)
        // Only update the calculated field to avoid overwriting user input during race conditions
        if (cFormValues.isFrom) {
          cFormValues.toAmount = resp.toAmount // User typed fromAmount, only update toAmount
        } else {
          cFormValues.fromAmount = resp.fromAmount // User typed toAmount, only update fromAmount
        }
        const activeKey = getActiveKey(cFormValues, maxSlippage)

        sliceState.setStateByKeys({
          activeKey,
          formValues: cloneDeep(cFormValues),
          formStatus: { ...cFormStatus, warning: resp.isExchangeRateLow ? 'warning-exchange-rate-low' : '' },
          exchangeOutput: {
            [activeKey]: { ...resp, loading: false, modal: getRouterWarningModal(resp, maxSlippage, cFormValues) },
          },
        })

        if (curve.signerAddress) {
          // validate fromAmount
          const userPoolBalances = await fetchPoolTokenBalances(config, curve, getAddress(pool.address))
          const userFromBalance = userPoolBalances[cFormValues.fromAddress]
          cFormValues.fromError = +userFromBalance >= +cFormValues.fromAmount ? '' : 'too-much'

          // update formValues only if activeKey hasn't changed during the async operation
          if (get()[SLICE_KEY].activeKey !== activeKey) return

          get()[SLICE_KEY].setStateByKey('formValues', cloneDeep(cFormValues))
          if (!cFormValues.fromError) {
            get()[SLICE_KEY].setStateByActiveKey('formEstGas', activeKey, {
              ...(get()[SLICE_KEY].formEstGas[storedActiveKey] ?? DEFAULT_EST_GAS),
              loading: true,
            })
            void get()[SLICE_KEY].fetchEstGasApproval(activeKey, curve.chainId, pool, cFormValues, maxSlippage)
          }
        }
      }
    },
    fetchMaxAmount: async (
      activeKey: string,
      config: Config,
      curve: CurveApi,
      pool: PoolTemplate,
      formValues: FormValues,
      maxSlippage: string,
    ) => {
      // stored values
      const userPoolBalances = await fetchPoolTokenBalances(config, curve, getAddress(pool.address))
      const walletFromBalance = userPoolBalances[formValues.fromAddress]
      const { basePlusPriority } = await fetchGasInfoAndUpdateLib({ chainId: curve.chainId })

      let fromAmount: string = walletFromBalance ?? '0'

      // get max amount for native token
      if (
        formValues.fromAddress.toLowerCase() === ethAddress &&
        typeof basePlusPriority?.[0] !== 'undefined' &&
        +fromAmount > 0
      ) {
        get()[SLICE_KEY].setStateByKey('isMaxLoading', true)
        const resp = await curvejsApi.poolSwap.estGasApproval(
          activeKey,
          curve.chainId,
          pool,
          formValues.isWrapped,
          formValues.toAddress,
          formValues.fromAddress,
          fromAmount,
          maxSlippage,
        )

        if (resp.estimatedGas) {
          fromAmount = getMaxAmountMinusGas(resp.estimatedGas, basePlusPriority[0], walletFromBalance)
        }
        get()[SLICE_KEY].setStateByKey('isMaxLoading', false)
      }

      return fromAmount
    },
    setFormValues: async (config, curve, pool, updatedFormValues, isGetMaxFrom, isSeed, maxSlippage) => {
      // stored values
      const storedActiveKey = get()[SLICE_KEY].activeKey
      const storedFormStatus = get()[SLICE_KEY].formStatus
      const storedFormValues = get()[SLICE_KEY].formValues

      // update form values
      const cFormValues = cloneDeep({ ...storedFormValues, ...updatedFormValues })
      cFormValues.toError = ''
      cFormValues.fromError = ''

      let activeKey = getActiveKey(cFormValues, maxSlippage)
      get()[SLICE_KEY].setStateByKeys({
        activeKey,
        formStatus: { ...DEFAULT_FORM_STATUS, isApproved: storedFormStatus.isApproved },
        formValues: cloneDeep(cFormValues),
      })

      if (
        !curve ||
        !pool ||
        isSeed == null ||
        !cFormValues.fromToken ||
        !cFormValues.fromAddress ||
        !cFormValues.toToken ||
        !cFormValues.toAddress ||
        cFormValues.isWrapped === null
      )
        return

      // get max fromAmount
      if (isGetMaxFrom) {
        cFormValues.fromAmount = await get()[SLICE_KEY].fetchMaxAmount(
          activeKey,
          config,
          curve,
          pool,
          cFormValues,
          maxSlippage,
        )
        activeKey = getActiveKey(cFormValues, maxSlippage)
        get()[SLICE_KEY].setStateByKeys({ activeKey, formValues: cloneDeep(cFormValues) })
      }

      if (!(+cFormValues.toAmount > 0 || +cFormValues.fromAmount > 0)) return

      // validate toAmount: If have toAmount and isFrom is false, confirm toAmount is not bigger than currency reserves
      if (+cFormValues.toAmount > 0 && !cFormValues.isFrom) {
        const currencyReserve = await fetchPoolCurrencyReserves({
          chainId: curve.chainId,
          poolAddress: getAddress(pool.address),
          isWrapped: cFormValues.isWrapped,
          useApi: !curve.signerAddress,
        })

        if (Array.isArray(currencyReserve?.tokens)) {
          cFormValues.toError = getReservesBalanceError(currencyReserve, cFormValues.toAddress, cFormValues.toAmount)
          get()[SLICE_KEY].setStateByKey('formValues', cloneDeep(cFormValues))
        }
      }

      if (cFormValues.toError) return

      // get exchange rate info
      get()[SLICE_KEY].setStateByActiveKey(
        'exchangeOutput',
        activeKey,
        cloneDeep({ ...DEFAULT_EXCHANGE_OUTPUT, loading: true }),
      )
      void get()[SLICE_KEY].fetchExchangeOutput(
        activeKey,
        storedActiveKey,
        config,
        curve,
        pool,
        cFormValues,
        maxSlippage,
      )
    },

    // steps
    fetchEstGasApproval: async (activeKey, chainId, pool, formValues, maxSlippage) => {
      const { fromAddress, toAddress, fromAmount, isWrapped } = formValues
      const resp = await curvejsApi.poolSwap.estGasApproval(
        activeKey,
        chainId,
        pool,
        isWrapped,
        fromAddress,
        toAddress,
        fromAmount,
        maxSlippage,
      )

      // set estimate gas state
      get()[SLICE_KEY].setStateByActiveKey('formEstGas', activeKey, { estimatedGas: resp.estimatedGas, loading: false })

      // set form status
      const storedFormStatus = get()[SLICE_KEY].formStatus
      if (!storedFormStatus.formProcessing) {
        get()[SLICE_KEY].setStateByKey('formStatus', {
          ...get()[SLICE_KEY].formStatus,
          isApproved: resp.isApproved,
          error: storedFormStatus.error || resp.error,
        })
      }
      return resp
    },
    fetchStepApprove: async (activeKey, config, curve, pool, formValues, maxSlippage) => {
      const { provider } = useWallet.getState()
      if (!provider) return setMissingProvider(get()[SLICE_KEY])

      const storedActiveKey = get()[SLICE_KEY].activeKey
      get()[SLICE_KEY].setStateByKey('formStatus', {
        ...get()[SLICE_KEY].formStatus,
        formProcessing: true,
        step: 'APPROVAL',
      })
      const { fromAddress, fromAmount, isWrapped } = formValues
      const resp = await curvejsApi.poolSwap.swapApprove(activeKey, provider, pool, isWrapped, fromAddress, fromAmount)
      if (resp.activeKey === get()[SLICE_KEY].activeKey) {
        const cFormStatus = cloneDeep(get()[SLICE_KEY].formStatus)
        cFormStatus.formProcessing = false
        cFormStatus.step = ''
        cFormStatus.error = ''

        if (resp.error) {
          cFormStatus.error = resp.error
          get()[SLICE_KEY].setStateByKey('formStatus', cFormStatus)
        } else {
          cFormStatus.formTypeCompleted = 'APPROVE'
          cFormStatus.isApproved = true
          get()[SLICE_KEY].setStateByKey('formStatus', cFormStatus)

          // fetch est gas, approval and exchange
          void get()[SLICE_KEY].fetchEstGasApproval(activeKey, curve.chainId, pool, formValues, maxSlippage)
          await get()[SLICE_KEY].fetchExchangeOutput(
            activeKey,
            storedActiveKey,
            config,
            curve,
            pool,
            formValues,
            maxSlippage,
          )
        }

        return resp
      }
    },
    fetchStepSwap: async (activeKey, curve, pool, formValues, maxSlippage) => {
      const { provider } = useWallet.getState()
      if (!provider) return setMissingProvider(get()[SLICE_KEY])

      get()[SLICE_KEY].setStateByKey('formStatus', {
        ...get()[SLICE_KEY].formStatus,
        formProcessing: true,
        step: 'SWAP',
      })
      const { fromAddress, fromAmount, toAddress, isWrapped } = formValues
      const resp = await curvejsApi.poolSwap.swap(
        activeKey,
        provider,
        pool,
        isWrapped,
        fromAddress,
        toAddress,
        fromAmount,
        maxSlippage,
      )
      if (resp.activeKey === get()[SLICE_KEY].activeKey) {
        const cFormStatus = cloneDeep(get()[SLICE_KEY].formStatus)
        cFormStatus.formProcessing = false
        cFormStatus.step = ''
        cFormStatus.error = ''

        if (resp.error) {
          cFormStatus.error = resp.error
          get()[SLICE_KEY].setStateByKey('formStatus', cFormStatus)
        } else {
          const cFormValues = cloneDeep(formValues)
          cFormValues.fromAmount = ''
          cFormValues.toAmount = ''

          cFormStatus.formTypeCompleted = 'SWAP'

          get()[SLICE_KEY].setStateByKeys({
            formStatus: cFormStatus,
            activeKey: getActiveKey(cFormValues, maxSlippage),
            exchangeOutput: {},
            formEstGas: {},
            formValues: cFormValues,
          })

          const params = {
            chainId: curve.chainId,
            poolAddress: getAddress(pool.address),
            userAddress: curve.signerAddress,
          }
          await Promise.all([invalidateUserPoolInfo(params), invalidatePoolInfo(params)])
        }
        return resp
      }
    },

    // slice helpers
    setStateByActiveKey: <T>(key: StateKey, activeKey: string, value: T) => {
      if (Object.keys(get()[SLICE_KEY][key]).length > 30) {
        get().setAppStateByKey(SLICE_KEY, key, { [activeKey]: value })
      } else {
        get().setAppStateByActiveKey(SLICE_KEY, key, activeKey, value)
      }
    },
    setStateByKey: <T>(key: StateKey, value: T) => {
      get().setAppStateByKey(SLICE_KEY, key, value)
    },
    setStateByKeys: (sliceState: Partial<SliceState>) => {
      get().setAppStateByKeys(SLICE_KEY, sliceState)
    },
    resetState: (pool, isWrapped) => {
      const { tokens, tokenAddresses } = getTokens(pool, { wrapped: isWrapped })
      get().resetAppState(SLICE_KEY, {
        ...DEFAULT_STATE,
        formValues: {
          ...DEFAULT_FORM_VALUES,
          isWrapped,
          fromToken: tokens[0],
          fromAddress: tokenAddresses[0],
          toToken: tokens[1],
          toAddress: tokenAddresses[1],
        },
      })
    },
  },
})

function getRouterWarningModal(
  {
    isExchangeRateLow,
    priceImpact,
    toAmount,
    fromAmount,
    fetchedToAmount,
  }: Pick<RoutesAndOutput, 'isExchangeRateLow' | 'priceImpact' | 'toAmount' | 'fromAmount' | 'fetchedToAmount'>,
  maxSlippage: string,
  { toToken }: FormValues,
) {
  const { isHighImpact } = getSlippageImpact({ maxSlippage, toAmount, priceImpact, fetchedToAmount })
  const swapModalProps = getSwapActionModalType(isHighImpact, isExchangeRateLow)
  const exchangeRate = (+toAmount / +fromAmount).toString()
  const exchangeValues = { toAmount, toToken }
  const modalType = {
    lowExchangeRate: { lowExchangeRate: true, title: swapModalProps.title, exchangeRate, ...exchangeValues },
    priceImpact: { priceImpact: true, title: swapModalProps.title, value: priceImpact, ...exchangeValues },
    priceImpactLowExchangeRate: {
      priceImpactLowExchangeRate: true,
      title: swapModalProps.title,
      value: priceImpact,
      exchangeRate,
      ...exchangeValues,
    },
  } as const

  if (swapModalProps.type && swapModalProps.type in modalType) {
    return modalType[swapModalProps.type]
  }
  return null
}

function getReservesBalanceError(currencyReserves: CurrencyReserves, address: string, amount: string) {
  if (+amount > 0) {
    const found = currencyReserves.tokens.find(t => t.tokenAddress === address)
    return found ? (+amount > 0 && +amount > +found.balance ? 'too-much-reserves' : '') : ''
  }
  return ''
}

function getActiveKey({ fromAddress, fromAmount, toAddress }: FormValues, maxSlippage: string) {
  const parsedFromAddress = fromAddress ? fromAddress.slice(fromAddress.length - 4) : ''
  const parsedToAddress = toAddress ? toAddress.slice(toAddress.length - 4) : ''
  return `${parsedFromAddress}-${fromAmount}-${parsedToAddress}-${maxSlippage}`
}
